/**
 * Reorder operations — DnD index updates.
 */

import type { Transaction } from "dexie"
import { getDb, getNow } from "./runtime.js"
import { ObjectStatus, ObjectType } from "./db-types.js"
import { nextIndex } from "./utils.js"
import { ok, fail } from "./op-types.js"
import type { OpResult } from "./op-types.js"
import { cascadeDescendants } from "./helpers/cascadeHelpers.js"
import { resolveContainerRealmId } from "./helpers/realmHelpers.js"
import { serializeTaskMoves } from "./helpers/queueHelpers.js"

// Re-export from helpers for callers that import from reorderOps
export {
  computeGhostIndex,
  getRealNeighborIndexes,
} from "./helpers/indexHelpers.js"

// ---------------------------------------------------------------------------
// reorderTask — single-field index update
// ---------------------------------------------------------------------------

export async function reorderTask(
  id: string,
  newIndex: number,
  field: "index" | "startAtIndex" = "index",
): Promise<OpResult> {
  const db = getDb()
  try {
    const timestamp = getNow()

    // An id with no object (or, for a schedule order, no schedule) behind it is not "reordered"
    if (!(await db.totodo.get(id))) return fail(`reorderTask: task not found: ${id}`)

    if (field === "startAtIndex") {
      const entryId = `#${id}`
      if (!(await db.startAtIndex.get(entryId))) return fail(`reorderTask: task is not scheduled: ${id}`)
      await db.startAtIndex.update(entryId, { index: newIndex })
      await db.totodo.update(id, { updatedAt: timestamp })
    } else {
      await db.totodo.update(id, { index: newIndex, updatedAt: timestamp })
    }

    return ok()
  } catch (e) {
    return fail(
      `reorderTask failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

// ---------------------------------------------------------------------------
// moveTaskToPosition — 0-based position among the siblings (for callers that do not know sort keys)
// ---------------------------------------------------------------------------

/**
 * Task siblings renumbered each side of the target when no sort key fits between the two neighbours
 * (the first window; see RENUMBER_WINDOW_MAX).
 */
const RENUMBER_WINDOW = 50
/** The window never grows beyond this many tasks each side (it doubles from RENUMBER_WINDOW while there is no room). */
const RENUMBER_WINDOW_MAX = 400

/**
 * Put `id` at the 0-based `position` among its VISIBLE siblings: the live, unfinished objects under
 * the same parentPath, in the order the app shows them (by `index`). Sections and sub-projects count
 * - they are on screen - a task's own checklist rows do not live there, and completed / canceled
 * siblings do not count: the app lists them in the logged items, not among the open ones. A position
 * past either end is clamped. reorderTask takes a raw sort key (what drag and drop computes); this
 * takes the position an AI client or an API user can reason about, and turns it into a key between
 * the neighbours.
 *
 * When no key fits between them (equal indexes, precision gone), only the TASKS around the target are
 * renumbered - never a section, project or area, which the caller of a task tool may not touch - and
 * only a bounded window of them (RENUMBER_WINDOW each side, doubling up to RENUMBER_WINDOW_MAX while the
 * siblings that stay as they are leave no room), spread between those siblings. If there is still no
 * room the move is refused with the reason.
 */
export async function moveTaskToPosition(
  id: string,
  position: number,
): Promise<OpResult<{ position: number; total: number }>> {
  const db = getDb()
  try {
    if (!Number.isFinite(position)) return fail("position must be a number")
    const task = await db.totodo.get(id)
    if (!task || task.isDeleted === 1) return fail(`moveTaskToPosition: task not found: ${id}`)

    const visible = (await db.totodo.where("[parentPath+isDeleted]").equals([task.parentPath, 0]).toArray())
      .filter((o) => o.id !== id && o.type !== ObjectType.Checklist && o.status === ObjectStatus.Unfinished)
      .sort((a, b) => a.index - b.index)
    const target = Math.min(Math.max(Math.trunc(position), 0), visible.length)
    const prev = visible[target - 1]
    const next = visible[target]

    const index = nextIndex(prev?.index, next?.index)
    const timestamp = getNow()
    const fits = (prev === undefined || index > prev.index) && (next === undefined || index < next.index)
    const result = { position: target, total: visible.length + 1 }
    if (fits) {
      await db.totodo.update(id, { index, updatedAt: timestamp })
      return ok(result)
    }

    // No key between the neighbours: the run of tasks around the target (sections, projects and areas
    // end it) gets new keys between the nearest siblings that stay as they are. The window starts at
    // RENUMBER_WINDOW tasks each side and doubles while the fixed neighbours leave no room (up to
    // RENUMBER_WINDOW_MAX), so a local collision costs a local rewrite.
    const isTask = (o: { type: ObjectType }) => o.type === ObjectType.Task || o.type === ObjectType.Subtask
    let order: typeof visible = []
    let keys: number[] | undefined
    for (let radius = RENUMBER_WINDOW; keys === undefined; radius *= 2) {
      let lo = target
      while (lo > 0 && target - lo < radius && isTask(visible[lo - 1]!)) lo--
      let hi = target
      while (hi < visible.length && hi - target < radius && isTask(visible[hi]!)) hi++
      const before = visible[lo - 1]
      const after = visible[hi]
      order = [...visible.slice(lo, target), task, ...visible.slice(target, hi)]
      if (before && after) {
        const step = (after.index - before.index) / (order.length + 1)
        if (step > 1e-9) keys = order.map((_, i) => before.index + step * (i + 1))
      } else if (before) {
        keys = order.map((_, i) => before.index + (i + 1))
      } else if (after) {
        keys = order.map((_, i) => after.index - (order.length - i))
      } else {
        keys = order.map((_, i) => i)
      }
      if (keys === undefined && radius >= RENUMBER_WINDOW_MAX) {
        return fail(
          "moveTaskToPosition: the siblings that cannot be moved here (sections or projects, or tasks beyond the " +
            "renumbered run) leave no room for the task between them. Move the task in the app, or reorder around it.",
        )
      }
    }
    const newKeys = keys

    await db.transaction("rw", [db.totodo], async () => {
      for (let i = 0; i < order.length; i++) {
        const o = order[i]!
        await db.totodo.update(o.id as string, o.id === id ? { index: newKeys[i]!, updatedAt: timestamp } : { index: newKeys[i]! })
      }
    })
    return ok(result)
  } catch (e) {
    return fail(`moveTaskToPosition failed: ${e instanceof Error ? e.message : String(e)}`)
  }
}

// ---------------------------------------------------------------------------
// reorderTaskDual — primary + ghost in one transaction
// ---------------------------------------------------------------------------

export async function reorderTaskDual(
  id: string,
  primaryIndex: number,
  primaryField: "index" | "startAtIndex",
  ghostIndex: number,
  ghostField: "index" | "startAtIndex",
): Promise<OpResult> {
  const db = getDb()
  try {
    const timestamp = getNow()
    const entryId = `#${id}`

    await db.transaction("rw", [db.totodo, db.startAtIndex], async () => {
      // Write primary
      if (primaryField === "startAtIndex") {
        await db.startAtIndex.update(entryId, { index: primaryIndex })
      } else {
        await db.totodo.update(id, { index: primaryIndex })
      }

      // Write ghost
      if (ghostField === "startAtIndex") {
        const existing = await db.startAtIndex.get(entryId)
        if (existing) {
          await db.startAtIndex.update(entryId, { index: ghostIndex })
        }
      } else {
        await db.totodo.update(id, { index: ghostIndex })
      }

      // Touch updatedAt once
      await db.totodo.update(id, { updatedAt: timestamp })
    })

    return ok()
  } catch (e) {
    return fail(
      `reorderTaskDual failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

// ---------------------------------------------------------------------------
// Container change — the parts of moveTask a cross-group drag needs too
// ---------------------------------------------------------------------------

interface ContainerChange {
  oldParentPath: string
  realmId: string
  realmChanged: boolean
}

/**
 * Read before the transaction: what moving `id` to `newParentPath` changes (null = nothing).
 * Callers read it inside serializeTaskMoves, so no other move of the task can change its path
 * between this read and the cascade.
 */
async function resolveContainerChange(
  id: string,
  newParentPath: string,
): Promise<ContainerChange | null> {
  const task = await getDb().totodo.get(id)
  if (!task || task.parentPath === newParentPath) return null
  const realmId = await resolveContainerRealmId(newParentPath)
  return {
    oldParentPath: task.parentPath,
    realmId,
    realmChanged: realmId !== task.realmId,
  }
}

/**
 * Set the new parentPath and, like moveTask, take the realm and every descendant
 * (checklist rows live under `${parentPath}/${id}`) along. The descendants go first:
 * dexie-sqlite does not roll back a failed Dexie transaction, and a task whose rows
 * stayed behind would show an empty checklist.
 */
async function applyContainerChange(
  id: string,
  newParentPath: string,
  change: ContainerChange | null,
  tx: Transaction,
): Promise<void> {
  if (change) {
    await cascadeDescendants(
      id,
      change.oldParentPath,
      newParentPath,
      change.realmId,
      tx,
    )
  }
  await getDb().totodo.update(id, {
    parentPath: newParentPath,
    ...(change?.realmChanged ? { realmId: change.realmId } : {}),
  })
}

// ---------------------------------------------------------------------------
// crossGroupReorder — move to new container + dual index
// ---------------------------------------------------------------------------

export async function crossGroupReorder(
  id: string,
  newParentPath: string,
  primaryIndex: number,
  primaryField: "index" | "startAtIndex",
  ghostIndex: number,
  ghostField: "index" | "startAtIndex",
): Promise<OpResult> {
  const db = getDb()
  try {
    const timestamp = getNow()
    const entryId = `#${id}`

    // After any other move of this task (see serializeTaskMoves)
    await serializeTaskMoves([id], async () => {
      const change = await resolveContainerChange(id, newParentPath)

      await db.transaction("rw", [db.totodo, db.startAtIndex], async (tx) => {
        // Move to new container (with its checklist and realm)
        await applyContainerChange(id, newParentPath, change, tx)

        // Write primary index
        if (primaryField === "startAtIndex") {
          await db.startAtIndex.update(entryId, { index: primaryIndex })
        } else {
          await db.totodo.update(id, { index: primaryIndex })
        }

        // Write ghost index
        if (ghostField === "startAtIndex") {
          const existing = await db.startAtIndex.get(entryId)
          if (existing) {
            await db.startAtIndex.update(entryId, { index: ghostIndex })
          }
        } else {
          await db.totodo.update(id, { index: ghostIndex })
        }

        // Touch updatedAt once
        await db.totodo.update(id, { updatedAt: timestamp })
      })
    })

    return ok()
  } catch (e) {
    return fail(
      `crossGroupReorder failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

// ---------------------------------------------------------------------------
// bulkReorderDual — multi-item dual-index write (multi-drag)
// ---------------------------------------------------------------------------

export interface BulkReorderItem {
  id: string
  primaryIndex: number
  ghostIndex: number
  newParentPath?: string
}

export async function bulkReorderDual(
  items: BulkReorderItem[],
  primaryField: "index" | "startAtIndex",
  ghostField: "index" | "startAtIndex",
): Promise<OpResult> {
  const db = getDb()
  if (items.length === 0) return ok()

  try {
    const timestamp = getNow()

    // After any other move of one of the moved tasks (see serializeTaskMoves)
    const moving = items.filter((item) => item.newParentPath).map((item) => item.id)
    await serializeTaskMoves(moving, async () => {
      const changes = new Map<string, ContainerChange | null>()
      for (const item of items) {
        if (item.newParentPath) {
          changes.set(item.id, await resolveContainerChange(item.id, item.newParentPath))
        }
      }

      await db.transaction("rw", [db.totodo, db.startAtIndex], async (tx) => {
        for (const item of items) {
          const entryId = `#${item.id}`

          // Optional container move (with its checklist and realm)
          if (item.newParentPath) {
            await applyContainerChange(
              item.id,
              item.newParentPath,
              changes.get(item.id) ?? null,
              tx,
            )
          }

          // Write primary index
          if (primaryField === "startAtIndex") {
            await db.startAtIndex.update(entryId, { index: item.primaryIndex })
          } else {
            await db.totodo.update(item.id, { index: item.primaryIndex })
          }

          // Write ghost index
          if (ghostField === "startAtIndex") {
            const existing = await db.startAtIndex.get(entryId)
            if (existing) {
              await db.startAtIndex.update(entryId, { index: item.ghostIndex })
            }
          } else {
            await db.totodo.update(item.id, { index: item.ghostIndex })
          }

          // Touch updatedAt
          await db.totodo.update(item.id, { updatedAt: timestamp })
        }
      })
    })

    return ok()
  } catch (e) {
    return fail(
      `bulkReorderDual failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}
